import React, { memo, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Col, Layout, Row, Tooltip } from 'antd';
import { GetAssetReviewContentSuccessDto } from '@api/Api';
import { useDebounced } from '@helpers/useDebounced';
import LottieComponent from '@components/Lottie';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as LogoSvg } from '@assets/icons/logo-white.svg';
import { ReactComponent as DownloadSvg } from '@assets/icons/bulkActions/download.svg';
import { ReactComponent as LinkSvg } from '@assets/icons/dropdown/link.svg';
import { ReactComponent as ArrowDownSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as FolderSvg } from '@assets/icons/folder.svg';
import { ReactComponent as PrivateFolderSvg } from '@assets/icons/folder-private.svg';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import { downloadAll } from '@helpers/download';

import {
  filterAssetsBySearchText,
  groupByFolder
} from '@pages/Campaigns/CampaignReview/utils';

import ControlPanel from '@pages/ExternalReviewer/ControlPanel';

import TilesView from './components/TilesView/TilesView';
import ListView from './components/ListView/ListView';

import './AssetsList.less';

const { Header, Content } = Layout;

type _AssetsListProps = {
  content: GetAssetReviewContentSuccessDto | undefined;
};

const SEARCH_PARAM = 'search';
const VIEW_PARAM = 'view';
const FOLDER_PARAM = 'folder';

export default memo(function AssetsList({ content }: _AssetsListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = (searchParams.get(SEARCH_PARAM) ?? '')
    .trim()
    .toLowerCase();
  const viewValue = searchParams.get(VIEW_PARAM) ?? 'tiles';
  const searchText = useDebounced(searchQuery, 250);
  const folderId = searchParams.get(FOLDER_PARAM) ?? '';

  const assetsGroupedByFolder = useMemo(() => {
    const assets = content?.assets || [];
    if (!assets.length) return { folders: [], withoutFolder: [] };
    return groupByFolder(assets);
  }, [content?.assets]);

  const selectedFolder = useMemo(() => {
    if (!folderId) return null;
    return (
      assetsGroupedByFolder.folders.find((folder) => folder.id === folderId) ??
      null
    );
  }, [assetsGroupedByFolder.folders, folderId]);

  const campaignName = content?.name || content?.campaignName;

  const folders = useMemo(() => {
    let _folders = assetsGroupedByFolder.folders || [];
    if (searchText) {
      _folders = _folders
        .map((folder) => {
          return {
            ...folder,
            items: filterAssetsBySearchText(folder.items, searchText)
          };
        })
        .filter((folder) => !!folder.items.length);
    }
    return _folders;
  }, [assetsGroupedByFolder.folders, searchText]);

  const assetsList = useMemo(() => {
    let _assets = assetsGroupedByFolder.withoutFolder || [];
    if (selectedFolder) {
      _assets = selectedFolder.items || [];
    }
    if (searchText) {
      _assets = filterAssetsBySearchText(_assets, searchText);
    }
    return _assets;
  }, [assetsGroupedByFolder.withoutFolder, searchText, selectedFolder]);

  const downloadAssets = useMemo(() => {
    const _assets = selectedFolder ? selectedFolder?.items : content?.assets;
    return (
      _assets?.filter(
        (asset) =>
          asset.asset?.permissions?.download &&
          asset.asset?.versions?.[0].sourceUrl
      ) || []
    ).map((asset) => asset.asset?.versions?.[0].sourceUrl);
  }, [content?.assets, selectedFolder]);

  const assetsDownloadUrls = useMemo(() => {
    return selectedFolder?.items
      .filter((asset) => !!asset.asset?.permissions.download)
      .map((asset) => asset.asset?.versions[0].sourceUrl);
  }, [selectedFolder?.items]);

  const isEmpty = useMemo(() => {
    return !assetsList?.length && !folders?.length && !folderId;
  }, [assetsList?.length, folders?.length, folderId]);

  const onSelectFolder = useCallback(
    (folderId: string) => {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set(FOLDER_PARAM, folderId);
      setSearchParams(updatedParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const onGoBack = useCallback(() => {
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete(FOLDER_PARAM);
    setSearchParams(updatedParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const onDownloadFiles = useCallback(() => {
    if (!downloadAssets.length) return;
    downloadAll(downloadAssets, `${campaignName}.zip`);
  }, [downloadAssets, campaignName]);

  return (
    <Layout
      style={{ background: '#1A1C1E', color: '#fff', minHeight: '100vh' }}
    >
      <Layout
        className="site-layout external-reviewer-media"
        style={{ background: '#1A1C1E' }}
      >
        <Header
          className="site-layout-background b-background-black"
          style={{
            padding: 0,
            width: '100%'
          }}
        >
          <div className="assets-list-header">
            <Row align="middle" justify="space-between">
              <Col>
                <LogoSvg />
              </Col>
              {!!downloadAssets.length && (
                <Col>
                  <Button
                    onClick={onDownloadFiles}
                    type="primary"
                    icon={<DownloadSvg />}
                  >
                    Download files
                  </Button>
                </Col>
              )}
            </Row>
          </div>
        </Header>
        <Content style={{ marginTop: 70 }}>
          <div className="assets-list">
            {selectedFolder && (
              <>
                <div className="assets-list-breadcrumbs">
                  <Button onClick={onGoBack}>
                    <ArrowLeftSvg />
                  </Button>
                  <div
                    className="assets-list-breadcrumbs-item"
                    onClick={onGoBack}
                  >
                    <Tooltip
                      title={campaignName}
                      overlayClassName="external_reviewer_tooltip"
                    >
                      {campaignName}
                    </Tooltip>
                  </div>
                  <ArrowDownSvg className="assets-list-breadcrumbs-separator" />
                  <div className="assets-list-breadcrumbs-item b-current">
                    <Tooltip
                      title={selectedFolder.name}
                      overlayClassName="external_reviewer_tooltip"
                    >
                      {selectedFolder.name}
                    </Tooltip>
                  </div>
                </div>
                <div className="assets-list-title">
                  {selectedFolder.isPrivate ? (
                    <PrivateFolderSvg />
                  ) : (
                    <FolderSvg width={32} height={32} />
                  )}
                  {selectedFolder.name}
                  <div>
                    <Button
                      type="text"
                      size="small"
                      className="assets-list-btn-download"
                      disabled={!assetsDownloadUrls?.length}
                      icon={<DownloadInvoiceSvg />}
                      onClick={() => {
                        if (!assetsDownloadUrls?.length) return;
                        downloadAll(
                          assetsDownloadUrls,
                          `${selectedFolder.name}.zip`
                        );
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            {!selectedFolder && (
              <div className="assets-list-title">
                <LinkSvg width={32} height={32} />
                {campaignName}
              </div>
            )}
            <ControlPanel />
            {viewValue === 'list' && (
              <ListView
                onSelectFolder={onSelectFolder}
                folders={folders}
                assetsList={assetsList}
                isOpenFolder={!!selectedFolder}
              />
            )}
            {viewValue === 'tiles' && (
              <TilesView
                onSelectFolder={onSelectFolder}
                folders={folders}
                assetsList={assetsList}
                isOpenFolder={!!selectedFolder}
              />
            )}
            {isEmpty && (
              <div className="assets-list-empty">
                <LottieComponent
                  className="assets-list-empty-icon"
                  view="addMediaInFolder"
                />
                <strong className="main-body-text main-body-text--bold">
                  No media found
                </strong>
              </div>
            )}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
});
